import { Pencil, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/cargofl-ui';
import { ApiError, toastFailure } from '@/lib/api';
import { ConfirmButton } from '@/components/confirm-dialog';
import { useCreateTag, useDeleteTag, useUpdateTag } from '@/lib/admin-api';
import { type TagRow, useTags } from '@/lib/catalog-api';

/**
 * Tags: free labels people put on entries, reportable alongside project and
 * activity.
 *
 * Unlike everything else on this screen, a tag can be deleted while in use - it
 * carries no hours, the entries survive untouched, and refusing to remove a
 * mistyped label would leave it in the picker forever. How many entries lost it is
 * reported, because that is the part somebody would not otherwise notice.
 */
export function TagsPanel() {
  const { data, isPending } = useTags();
  const remove = useDeleteTag();
  const rename = useUpdateTag();
  const [renaming, setRenaming] = React.useState<TagRow | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  if (isPending) return <Skeleton className="h-56 w-full" />;
  const rows = data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="ts-num text-sm text-muted-foreground">{rows.length} tags</span>
        <Button hint={"Add a tag people can put on their time entries"} onClick={() => setAdding(true)}>
          <Plus className="size-4" />
          Add a tag
        </Button>
      </div>

      {error && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}
      {notice && (
        <Alert>
          <AlertDescription className="text-sm leading-relaxed">{notice}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[460px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Tag</TableHead>
                  <TableHead className="text-right">Used on</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell>
                      <span className="inline-flex items-center gap-2 font-medium">
                        <span
                          className="inline-block size-3 rounded-full border"
                          style={tag.color ? { backgroundColor: tag.color } : undefined}
                        />
                        {tag.name}
                      </span>
                    </TableCell>
                    <TableCell className="ts-num text-right">
                      {tag._count.entries} {tag._count.entries === 1 ? 'entry' : 'entries'}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        {/* Rename, because the only other way to fix a typo was to DELETE the tag -
                            which strips it off every entry that carries it. A mistake whose only
                            remedy is destructive is a trap, not a workflow. */}
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Rename tag"
                          onClick={() => setRenaming(tag)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <ConfirmButton
                          variant="ghost"
                          size="icon"
                          aria-label="Delete tag"
                          disabled={remove.isPending}
                          pending={remove.isPending}
                          title={`Delete the tag "${tag.name}"?`}
                          description={
                            <>
                              It is taken off every entry that carries it. The hours
                              themselves are untouched, but the label is gone and cannot be
                              put back without re-tagging each entry.
                            </>
                          }
                          confirmLabel="Delete tag"
                          destructive
                          onConfirm={() => {
                            setError(null);
                            setNotice(null);
                            remove.mutate(tag.id, {
                              onSuccess: (r) => {
                                const n = (r as { removedFrom?: number })?.removedFrom ?? 0;
                                setNotice(
                                  n > 0
                                    ? `Deleted "${tag.name}". It was removed from ${n} ${n === 1 ? 'entry' : 'entries'} — the hours are untouched.`
                                    : `Deleted "${tag.name}".`,
                                );
                              },
                              onError: (e) => {
                                setError(e instanceof ApiError ? e.message : 'Could not delete that.');
                                toastFailure(`Could not delete "${tag.name}"`)(e);
                              },
                            });
                          }}
                        >
                          <Trash2 className="size-4" />
                        </ConfirmButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                      No tags yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {adding && <AddTagDialog onClose={() => setAdding(false)} />}
    </div>
  );
}

function AddTagDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateTag();
  const [name, setName] = React.useState('');
  const [color, setColor] = React.useState('#008a5c');
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ name, color });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create that tag.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a tag</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tag-name">Name</Label>
            <Input id="tag-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tag-color">Colour</Label>
            <div className="flex items-center gap-2">
              <Input
                id="tag-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-9 w-16 p-1"
              />
              <span className="ts-num text-xs text-muted-foreground">{color}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Used to tint the chip wherever the tag appears.
            </p>
          </div>
          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Adding…' : 'Add tag'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Rename a tag in place.
 *
 * Every entry keeps the tag - only its label changes. That is the whole point:
 * before this, correcting "Devlopment" meant deleting the tag, which took it off
 * every entry that used it, and then re-tagging them by hand.
 */
function RenameTagDialog({
  tag,
  onClose,
  onSave,
  busy,
}: {
  tag: TagRow | null;
  onClose: () => void;
  onSave: (name: string) => void | Promise<void>;
  busy: boolean;
}) {
  const [name, setName] = React.useState('');
  React.useEffect(() => setName(tag?.name ?? ''), [tag]);

  return (
    <Dialog open={tag !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename &ldquo;{tag?.name}&rdquo;</DialogTitle>
          <DialogDescription>
            {tag?._count.entries
              ? `The ${tag._count.entries} ${tag._count.entries === 1 ? 'entry' : 'entries'} carrying it keep it — only the label changes.`
              : 'Nothing carries this tag yet.'}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onSave(name.trim());
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="tag-name">Name</Label>
            <Input id="tag-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || name.trim() === '' || name.trim() === tag?.name}>
              {busy ? 'Renaming…' : 'Rename'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
